import React, { useEffect, useState, useCallback } from 'react';
import { Button, Text, Note } from '@freee_jp/vibes';
import {
  fetchDealProposals, approveDealProposal, rejectDealProposal, updateDealProposal,
  fetchAccountItemsMap, fetchSectionsMap, fetchTaxCodesMap, DealProposal,
} from '../api/client';
import { resolveAccountLabel } from '../utils/accountDisplay';

// ---------------------------------------------------------------------------
// Deal Proposals Panel
//
// Enforces MASTER_PIPELINE_RULES §0.1.1: Claude may NOT register a deal with
// freee until the user explicitly approves it here. Each proposal lists
// partner, amount, detail rows (account/tax/section/amount) and PDF link, and
// offers Approve / Reject buttons. Approve transitions the proposal to
// 'approved'; Claude then polls and executes freee.deal_create.
// ---------------------------------------------------------------------------

interface Props {
  open: boolean;
  onClose: () => void;
  onApprovedCountChange?: (n: number) => void;
}

// --- 行編集フォーム用 (Phase 0: updateDealProposal 結線) ---------------------
// controlled input のため数値系も string で保持 ('' = 未選択/未入力)。
interface EditRow {
  account_item_id: string;
  tax_code: string;
  section_id: string;      // '' = 部門なし
  item_id: number | null;  // 編集対象外 (元の値を pass-through)
  amount: string;
  description: string;
  entry_side?: string;     // 元 detail に存在すれば pass-through
}

const editInputStyle: React.CSSProperties = {
  fontSize: '0.75rem', padding: '3px 6px', border: '1px solid #d5d3d0',
  borderRadius: '4px', background: '#fff', color: '#252525', boxSizing: 'border-box',
};

// ID→名称解決 (マスタ未取得/未登録IDは生ID表示に fallback)
const resolveName = (map: Map<number, string> | null, id: any): string => {
  if (id === null || id === undefined || id === '') return '—';
  const n = Number(id);
  const name = map && Number.isFinite(n) ? map.get(n) : undefined;
  return name || String(id);
};

// マスタ select (マスタ未取得時は数値ID直入力に fallback)
const MasterSelect: React.FC<{
  map: Map<number, string> | null;
  value: string;
  onChange: (v: string) => void;
  emptyLabel: string;
  width: number | string;
}> = ({ map, value, onChange, emptyLabel, width }) => {
  if (!map || map.size === 0) {
    return (
      <input
        type="number"
        value={value}
        placeholder={emptyLabel}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...editInputStyle, width }}
      />
    );
  }
  const unknown = value !== '' && !map.has(Number(value));
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...editInputStyle, width }}>
      <option value="">{emptyLabel}</option>
      {unknown && <option value={value}>#{value}</option>}
      {Array.from(map.entries()).map(([id, name]) => (
        <option key={id} value={String(id)}>{name}</option>
      ))}
    </select>
  );
};

export const DealProposalsPanel: React.FC<Props> = ({ open, onClose, onApprovedCountChange }) => {
  const [proposals, setProposals] = useState<DealProposal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actingRef, setActingRef] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'proposed' | 'approved' | 'rejected' | 'registered'>('proposed');

  // 行編集フォーム state
  const [editingRef, setEditingRef] = useState<string | null>(null);
  const [editRows, setEditRows] = useState<EditRow[]>([]);
  const [editReason, setEditReason] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // freee マスタ (ID→名称)。取得失敗時は null のまま = 数値ID直入力 fallback
  const [accountItemsMap, setAccountItemsMap] = useState<Map<number, string> | null>(null);
  const [sectionsMap, setSectionsMap] = useState<Map<number, string> | null>(null);
  const [taxCodesMap, setTaxCodesMap] = useState<Map<number, string> | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchDealProposals(statusFilter)
      .then((r) => {
        setProposals(r.items);
        onApprovedCountChange?.(r.count);
      })
      .catch((e) => setError(e.message || 'fetch failed'))
      .finally(() => setLoading(false));
  }, [statusFilter, onApprovedCountChange]);

  useEffect(() => {
    if (!open) return;
    refresh();
    const id = setInterval(() => {
      if (editingRef === null) refresh(); // 編集中はポーリング停止 (フォーム消失防止)
    }, 5000);
    return () => clearInterval(id);
  }, [open, refresh, editingRef]);

  // マスタ名称解決 (client.ts 側 module cache 付きなので open 毎でも実質1回)
  useEffect(() => {
    if (!open) return;
    let alive = true;
    fetchAccountItemsMap().then((m) => { if (alive) setAccountItemsMap(m); }).catch(() => {});
    fetchSectionsMap().then((m) => { if (alive) setSectionsMap(m); }).catch(() => {});
    fetchTaxCodesMap().then((m) => { if (alive) setTaxCodesMap(m); }).catch(() => {});
    return () => { alive = false; };
  }, [open]);

  const doApprove = useCallback(async (ref: string) => {
    setActingRef(ref);
    try {
      await approveDealProposal(ref);
      setToast(`✓ 承認: ${ref}`);
      refresh();
    } catch (e: any) {
      setToast(`承認失敗: ${e.message}`);
    } finally {
      setActingRef(null);
      setTimeout(() => setToast(null), 4000);
    }
  }, [refresh]);

  const doReject = useCallback(async (ref: string) => {
    const reason = window.prompt(`却下理由 (${ref}):`);
    if (reason === null) return;
    setActingRef(ref);
    try {
      await rejectDealProposal(ref, reason || undefined);
      setToast(`✕ 却下: ${ref}`);
      refresh();
    } catch (e: any) {
      setToast(`却下失敗: ${e.message}`);
    } finally {
      setActingRef(null);
      setTimeout(() => setToast(null), 4000);
    }
  }, [refresh]);

  // --- 行編集 (proposed / approved のみ。registered は不可) -----------------
  const startEdit = useCallback((p: DealProposal) => {
    const details = Array.isArray(p.proposed_deal_json?.details) ? p.proposed_deal_json.details : [];
    const rows: EditRow[] = details.map((d: any) => ({
      account_item_id: (d.account_item_id ?? d.accountItemId) != null ? String(d.account_item_id ?? d.accountItemId) : '',
      tax_code: (d.tax_code ?? d.taxCode) != null ? String(d.tax_code ?? d.taxCode) : '',
      section_id: (d.section_id ?? d.sectionId) != null ? String(d.section_id ?? d.sectionId) : '',
      item_id: (d.item_id ?? d.itemId) != null ? Number(d.item_id ?? d.itemId) : null,
      amount: d.amount != null ? String(d.amount) : '',
      description: d.description || '',
      ...(d.entry_side ? { entry_side: String(d.entry_side) } : {}),
    }));
    setEditRows(rows.length > 0 ? rows : [{ account_item_id: '', tax_code: '', section_id: '', item_id: null, amount: '', description: '' }]);
    setEditReason('');
    setEditError(null);
    setEditingRef(p.ref_number);
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingRef(null);
    setEditRows([]);
    setEditReason('');
    setEditError(null);
  }, []);

  const setRowField = useCallback((idx: number, field: 'account_item_id' | 'tax_code' | 'section_id' | 'amount' | 'description', value: string) => {
    setEditRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  }, []);

  const addEditRow = useCallback(() => {
    setEditRows((rows) => [...rows, { account_item_id: '', tax_code: '', section_id: '', item_id: null, amount: '', description: '' }]);
  }, []);

  const removeEditRow = useCallback((idx: number) => {
    setEditRows((rows) => (rows.length <= 1 ? rows : rows.filter((_, i) => i !== idx)));
  }, []);

  // パネル閉/フィルタ切替時は編集を破棄 (対象 proposal がリストから消えるため)
  useEffect(() => {
    cancelEdit();
  }, [open, statusFilter, cancelEdit]);

  const doSaveEdit = useCallback(async (ref: string) => {
    const details: Array<{ account_item_id: number; tax_code: number; amount: number; section_id?: number | null; item_id?: number | null; description?: string | null; entry_side?: string }> = [];
    for (let i = 0; i < editRows.length; i++) {
      const r = editRows[i];
      const acc = parseInt(r.account_item_id, 10);
      const tax = parseInt(r.tax_code, 10);
      const amt = Math.round(Number(r.amount));
      if (!Number.isFinite(acc) || acc <= 0) { setEditError(`行${i + 1}: 勘定科目を選択してください`); return; }
      if (!Number.isFinite(tax) || tax < 0) { setEditError(`行${i + 1}: 税区分を選択してください`); return; }
      if (r.amount.trim() === '' || !Number.isFinite(amt) || amt === 0) { setEditError(`行${i + 1}: 金額を入力してください (0 以外)`); return; }
      const sec = r.section_id === '' ? null : parseInt(r.section_id, 10);
      if (sec !== null && !Number.isFinite(sec)) { setEditError(`行${i + 1}: 部門IDが不正です`); return; }
      details.push({
        account_item_id: acc,
        tax_code: tax,
        amount: amt,
        section_id: sec,
        item_id: r.item_id ?? null,
        description: r.description || null,
        ...(r.entry_side ? { entry_side: r.entry_side } : {}),
      });
    }
    if (details.length === 0) { setEditError('明細行がありません'); return; }
    setSavingEdit(true);
    setEditError(null);
    try {
      await updateDealProposal(ref, details, editReason.trim() || undefined);
      setToast(`✎ 更新: ${ref} (修正diffは学習へ還流)`);
      cancelEdit();
      refresh();
      setTimeout(() => setToast(null), 4000);
    } catch (e: any) {
      setEditError(e.message || 'update failed');
    } finally {
      setSavingEdit(false);
    }
  }, [editRows, editReason, refresh, cancelEdit]);

  const approveAll = useCallback(async () => {
    if (proposals.length === 0) return;
    if (!window.confirm(`${proposals.length}件すべてを承認しますか？`)) return;
    setLoading(true);
    for (const p of proposals) {
      try { await approveDealProposal(p.ref_number); } catch (e) { /* skip fails */ }
    }
    setToast(`${proposals.length}件を承認処理しました`);
    refresh();
    setTimeout(() => setToast(null), 4000);
  }, [proposals, refresh]);

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
        }}
      />
      <div
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: 'min(720px, 90vw)', background: '#fff', zIndex: 1001,
          boxShadow: '-8px 0 24px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
        role="complementary"
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e4e2', background: '#fafafa' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <Text weight="bold" size={1}>仕訳承認キュー (freee登録前)</Text>
              <div style={{ marginTop: '2px' }}>
                <Note>MASTER_PIPELINE_RULES §0.1.1: 承認後のみ freee 登録可</Note>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              <Button small appearance="tertiary" onClick={refresh}>更新</Button>
              <Button small appearance="tertiary" onClick={onClose}>閉じる</Button>
            </div>
          </div>
          <div style={{ marginTop: '10px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '0.75rem', color: '#6b6560' }}>ステータス:</span>
            {(['proposed', 'approved', 'rejected', 'registered'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                style={{
                  fontSize: '0.75rem', padding: '4px 10px', borderRadius: '12px',
                  border: '1px solid ' + (statusFilter === s ? '#2e7cf6' : '#d5d3d0'),
                  background: statusFilter === s ? '#ebf3ff' : '#fff',
                  color: statusFilter === s ? '#2e7cf6' : '#6b6560',
                  cursor: 'pointer', fontWeight: statusFilter === s ? 600 : 400,
                }}
              >
                {s === 'proposed' ? '未承認' : s === 'approved' ? '承認済' : s === 'rejected' ? '却下' : '登録完了'}
              </button>
            ))}
            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#6b6560' }}>
              {proposals.length}件
            </span>
            {statusFilter === 'proposed' && proposals.length > 0 && (
              <Button small appearance="primary" onClick={approveAll} disabled={loading}>
                一括承認 ({proposals.length})
              </Button>
            )}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 20px', background: '#fff' }}>
          {error && (
            <div style={{ padding: '10px', background: '#fdecec', color: '#c33939', borderRadius: '4px', marginBottom: '10px', fontSize: '0.8125rem' }}>
              エラー: {error}
            </div>
          )}

          {!loading && proposals.length === 0 && !error && (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#8a8580' }}>
              <div style={{ fontSize: '2rem', marginBottom: '12px' }}>✓</div>
              <div style={{ fontSize: '0.9rem' }}>
                {statusFilter === 'proposed' ? '対応待ち(承認)の仕訳はありません' : `${statusFilter} の仕訳はありません`}
              </div>
            </div>
          )}

          {proposals.map((p) => {
            const deal = p.proposed_deal_json || ({} as any);
            const details = Array.isArray(deal.details) ? deal.details : [];
            const sumAmount = details.reduce((s: number, d: any) => s + (d.amount || 0), 0);
            const pdfRowidInt = p.pdf_rowid ? parseInt(String(p.pdf_rowid), 10) : NaN;
            const pdfUrl = Number.isFinite(pdfRowidInt) && pdfRowidInt > 0
              ? `https://invoice-review.system-0f7.workers.dev/pdf/${pdfRowidInt}`
              : null;
            const isEditing = editingRef === p.ref_number;
            const editedSum = isEditing
              ? editRows.reduce((s, r) => s + (Math.round(Number(r.amount)) || 0), 0)
              : 0;

            return (
              <div
                key={p.id}
                style={{
                  border: '1px solid #e5e4e2', borderRadius: '6px',
                  padding: '14px', marginBottom: '10px', background: '#fff',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.7rem', background: '#f5f3f2', color: '#6b6560', padding: '2px 6px', borderRadius: '3px', fontFamily: 'monospace' }}>
                        {p.ref_number}
                      </span>
                      <Text weight="bold" size={0.875}>{p.partner_name || `partner_id: ${p.partner_id}`}</Text>
                      {pdfUrl && (
                        <a href={pdfUrl} target="_blank" rel="noreferrer" style={{ fontSize: '0.7rem', color: '#2e7cf6' }}>
                          PDF →
                        </a>
                      )}
                    </div>
                    <div style={{ marginTop: '4px', fontSize: '0.75rem', color: '#6b6560' }}>
                      発行: {p.issue_date || '—'} / 支払: {p.due_date || '—'} /
                      <b style={{ color: '#252525', marginLeft: '4px' }}>
                        合計 ¥{(p.amount ?? sumAmount).toLocaleString()}
                      </b>
                    </div>
                  </div>
                  {p.status === 'proposed' && !isEditing && (
                    <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                      <Button small appearance="primary" onClick={() => doApprove(p.ref_number)} disabled={actingRef === p.ref_number}>
                        {actingRef === p.ref_number ? '...' : '承認'}
                      </Button>
                      <Button small appearance="secondary" onClick={() => doReject(p.ref_number)} disabled={actingRef === p.ref_number}>
                        却下
                      </Button>
                      <Button small appearance="tertiary" onClick={() => startEdit(p)} disabled={actingRef === p.ref_number}>
                        ✎ 編集
                      </Button>
                    </div>
                  )}
                  {p.status === 'approved' && !isEditing && (
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexShrink: 0 }}>
                      <span style={{ fontSize: '0.7rem', color: '#338650', padding: '4px 8px', background: '#e6f4ea', borderRadius: '10px' }}>
                        ✓ 承認済 ({p.approved_by})
                      </span>
                      <Button small appearance="tertiary" onClick={() => startEdit(p)}>
                        ✎ 編集
                      </Button>
                    </div>
                  )}
                  {isEditing && (
                    <span style={{ fontSize: '0.7rem', color: '#8a5a00', padding: '4px 8px', background: '#fdf3e0', borderRadius: '10px', flexShrink: 0 }}>
                      ✎ 編集中
                    </span>
                  )}
                  {p.status === 'rejected' && (
                    <span style={{ fontSize: '0.7rem', color: '#c33939', padding: '4px 8px', background: '#fdecec', borderRadius: '10px' }}>
                      ✕ 却下
                    </span>
                  )}
                  {p.status === 'registered' && (
                    <span style={{ fontSize: '0.7rem', color: '#2e7cf6', padding: '4px 8px', background: '#ebf3ff', borderRadius: '10px' }}>
                      ✓ freee登録済 (deal#{p.freee_deal_id})
                    </span>
                  )}
                </div>

                {/* Details */}
                {isEditing ? (
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #e5e4e2' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#252525', marginBottom: '6px' }}>
                      明細行の編集
                      <span style={{ fontWeight: 400, color: '#8a8580', marginLeft: '6px' }}>(品目・タグは元の値を維持)</span>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', fontSize: '0.7rem', color: '#8a8580', marginBottom: '2px' }}>
                      <span style={{ width: 170 }}>勘定科目</span>
                      <span style={{ width: 150 }}>税区分</span>
                      <span style={{ width: 110 }}>部門</span>
                      <span style={{ width: 90, textAlign: 'right' }}>金額(税込)</span>
                      <span style={{ flex: 1 }}>摘要</span>
                      <span style={{ width: 30 }} />
                    </div>
                    {editRows.map((row, i) => (
                      <div key={i} style={{ display: 'flex', gap: '4px', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap' }}>
                        <MasterSelect
                          map={accountItemsMap} value={row.account_item_id}
                          onChange={(v) => setRowField(i, 'account_item_id', v)}
                          emptyLabel="勘定科目を選択" width={170}
                        />
                        <MasterSelect
                          map={taxCodesMap} value={row.tax_code}
                          onChange={(v) => setRowField(i, 'tax_code', v)}
                          emptyLabel="税区分を選択" width={150}
                        />
                        <MasterSelect
                          map={sectionsMap} value={row.section_id}
                          onChange={(v) => setRowField(i, 'section_id', v)}
                          emptyLabel="(部門なし)" width={110}
                        />
                        <input
                          type="number" value={row.amount} placeholder="金額"
                          onChange={(e) => setRowField(i, 'amount', e.target.value)}
                          style={{ ...editInputStyle, width: 90, textAlign: 'right' }}
                        />
                        <input
                          type="text" value={row.description} placeholder="摘要"
                          onChange={(e) => setRowField(i, 'description', e.target.value)}
                          style={{ ...editInputStyle, flex: 1, minWidth: 80 }}
                        />
                        <button
                          onClick={() => removeEditRow(i)}
                          disabled={editRows.length <= 1}
                          title="行削除"
                          style={{
                            fontSize: '0.75rem', padding: '3px 8px', border: '1px solid #d5d3d0',
                            borderRadius: '4px', background: '#fff',
                            color: editRows.length <= 1 ? '#c9c6c3' : '#c33939',
                            cursor: editRows.length <= 1 ? 'default' : 'pointer',
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                      <button
                        onClick={addEditRow}
                        style={{
                          fontSize: '0.75rem', padding: '3px 10px', border: '1px dashed #2e7cf6',
                          borderRadius: '4px', background: '#fff', color: '#2e7cf6', cursor: 'pointer',
                        }}
                      >
                        + 行追加
                      </button>
                      <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: '#6b6560' }}>
                        編集後合計 <b style={{ color: '#252525' }}>¥{editedSum.toLocaleString()}</b>
                        {p.amount != null && editedSum !== p.amount && (
                          <span style={{ color: '#c33939', marginLeft: '4px' }}>
                            (提案合計 ¥{p.amount.toLocaleString()} と不一致)
                          </span>
                        )}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '8px' }}>
                      <input
                        type="text" value={editReason} placeholder="修正理由 (任意・学習ログに記録)"
                        onChange={(e) => setEditReason(e.target.value)}
                        style={{ ...editInputStyle, flex: 1, minWidth: 0 }}
                      />
                      <Button small appearance="primary" onClick={() => doSaveEdit(p.ref_number)} disabled={savingEdit}>
                        {savingEdit ? '保存中...' : '保存'}
                      </Button>
                      <Button small appearance="tertiary" onClick={cancelEdit} disabled={savingEdit}>
                        キャンセル
                      </Button>
                    </div>
                    {editError && (
                      <div style={{ marginTop: '6px', padding: '6px 8px', background: '#fdecec', color: '#c33939', borderRadius: '4px', fontSize: '0.75rem' }}>
                        {editError}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px dashed #e5e4e2' }}>
                    <table style={{ width: '100%', fontSize: '0.7rem', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ color: '#8a8580', textAlign: 'left' }}>
                          <th style={{ padding: '2px 4px' }}>勘定科目</th>
                          <th style={{ padding: '2px 4px' }}>税区分</th>
                          <th style={{ padding: '2px 4px' }}>部門</th>
                          <th style={{ padding: '2px 4px' }}>品目</th>
                          <th style={{ padding: '2px 4px' }}>備考</th>
                          <th style={{ padding: '2px 4px', textAlign: 'right' }}>金額(税込)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {details.map((d: any, i: number) => (
                          <tr key={i} style={{ borderTop: '1px solid #f0efed' }}>
                            <td style={{ padding: '3px 4px' }}>{resolveAccountLabel(accountItemsMap, d.accountName ?? d.account_item_name, d.account_item_id ?? d.accountItemId)}</td>
                            <td style={{ padding: '3px 4px' }}>{resolveName(taxCodesMap, d.tax_code ?? d.taxCode)}</td>
                            <td style={{ padding: '3px 4px' }}>{d.sectionName || d.section_name || resolveName(sectionsMap, d.section_id ?? d.sectionId)}</td>
                            <td style={{ padding: '3px 4px' }}>{d.itemName || d.item_id || d.itemId || '—'}</td>
                            <td style={{ padding: '3px 4px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.description}>
                              {d.description || '—'}
                            </td>
                            <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 600 }}>
                              ¥{(d.amount || 0).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {p.rejection_reason && (
                  <div style={{ marginTop: '6px', fontSize: '0.7rem', color: '#c33939' }}>
                    却下理由: {p.rejection_reason}
                  </div>
                )}
              </div>
            );
          })}

          {loading && <div style={{ textAlign: 'center', padding: '20px', color: '#8a8580' }}>読込中...</div>}
        </div>

        {toast && (
          <div style={{
            position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
            background: '#252525', color: '#fff', padding: '10px 16px',
            borderRadius: '6px', fontSize: '0.8rem', zIndex: 2000,
          }}>
            {toast}
          </div>
        )}
      </div>
    </>
  );
};
